/* ============================================================
   Asha Books — Bible Recommendation Engine v2
   File: assets/bible-recommender.js

   Question flow (human-first):
   Q1  Who is this Bible for?            (who)
   Q2  How will it mainly be used?       (use)
   Q3  Preferred language?               (language)
   Q4  Specific translation?             (translation)  ← English path only
   Q5  What's your budget?               (budget)
   Q6  Print size?                       (printSize)
   Q7  Format / cover?                   (format)

   Behind the scenes:
   - translation answer maps directly to translation tags
   - who + use together derive experience and purpose tags
   - scoring engine handles English vs Indian path automatically
   ============================================================ */

(function () {
  'use strict';

  // ── Config ───────────────────────────────────────────────────────
  const CONFIG = {
    collectionHandle: window.brConfig?.collectionHandle || 'bibles',
    maxResults: window.brConfig?.maxResults || 3,
    trackingUrl: window.brConfig?.trackingUrl || '',
    aiUrl: window.brConfig?.aiUrl || '',
    minScore: 40,
    socialProof: window.brConfig?.socialProof || 0,
  };

  // ── Reading style → translation tag mapping ──────────────────────
  // User never sees translation names — they pick a reading style
  // and we match the right translations behind the scenes.
  const STYLE_TRANSLATIONS = {
    'very-easy': ['nlt', 'nirv', 'gnt', 'icb'],
    'modern': ['niv', 'nkjv', 'nlt', 'esv', 'nasb'],
    'traditional': ['kjv', 'nkjv'],
  };

  // ── Scoring weights — English (sum = 100) ────────────────────────
  const W = { purpose: 35, experience: 25, style: 25, budget: 15 };

  // ── Scoring weights — Indian (sum = 100) ─────────────────────────
  const W_IN = { language: 40, use: 30, who: 15, budget: 15 };

  // ── Expert boost rules ───────────────────────────────────────────
  // Encode your team's knowledge here. test(tags, answerMap, price) → bool
  const EXPERT_BOOSTS = [
    // NLT — easy to read, great for new believers
    {
      test: (t, a) => a.translation === 'nlt' && t.translation?.includes('nlt'),
      bonus: 12, reason: 'Popular with first-time Bible readers'
    },

    // NIrV — child/newcomer level (no translation question option, so boost on use/who)
    {
      test: (t, a) => !a.translation && a.who === 'child' && t.translation?.includes('nirv'),
      bonus: 10, reason: 'Written at a very easy reading level'
    },

    // GNT — natural everyday English
    {
      test: (t, a) => !a.translation && t.translation?.includes('gnt'),
      bonus: 6, reason: 'Clear, natural everyday language'
    },

    // NIV — world's most-read modern Bible
    {
      test: (t, a) => a.translation === 'niv' && t.translation?.includes('niv'),
      bonus: 10, reason: 'Most widely read modern translation'
    },

    // NKJV — classic feel, modern readability
    {
      test: (t, a) => a.translation === 'nkjv' && t.translation?.includes('nkjv'),
      bonus: 7, reason: 'Modern language with a classic feel'
    },

    // KJV — the timeless classic
    {
      test: (t, a) => a.translation === 'kjv' && t.translation?.includes('kjv'),
      bonus: 12, reason: 'The timeless King James Version'
    },

    // ESV — trusted for study and preaching
    {
      test: (t, a) => a.translation === 'esv' && t.translation?.includes('esv'),
      bonus: 10, reason: 'Word-for-word accuracy'
    },

    // Preaching → ESV is trusted by preachers (even without explicit ESV pick)
    {
      test: (t, a) => a.use === 'preaching' && !a.translation && t.translation?.includes('esv'),
      bonus: 12, reason: 'Trusted by pastors for preaching'
    },

    // Study → NASB is very literal, great for study
    {
      test: (t, a) => a.use === 'bible-study' && t.translation?.includes('nasb'),
      bonus: 10, reason: 'Highly literal and accurate'
    },

    // Study → NET has inline translator notes
    {
      test: (t, a) => a.use === 'bible-study' && t.translation?.includes('net'),
      bonus: 8, reason: 'Includes detailed translation notes'
    },

    // Child → NIrV is at a child's reading level
    {
      test: (t, a) => a.who === 'child' && t.translation?.includes('nirv'),
      bonus: 12, reason: 'Perfect reading level for children'
    },

    // Teenager → NIV is popular with youth
    {
      test: (t, a) => a.who === 'teenager' && t.translation?.includes('niv'),
      bonus: 8, reason: 'Popular with teens and young adults'
    },

    // Church/travel → thinline or compact are ideal
    {
      test: (t, a) => (a.use === 'church' || a.use === 'travel') && t.preference?.includes('thinline'),
      bonus: 8, reason: 'Slim and lightweight to carry'
    },
    {
      test: (t, a) => a.use === 'travel' && t.preference?.includes('compact'),
      bonus: 8, reason: 'Compact enough to take anywhere'
    },

    // Everyday reading → regular thinline is a comfortable size (but NOT pocket/compact)
    {
      test: (t, a) => a.use === 'everyday-reading' && t.preference?.includes('thinline') && !t.preference?.includes('compact'),
      bonus: 6, reason: 'A comfortable size for daily reading'
    },

    // Journaling Bible is a specific product type
    {
      test: (t, a) => a.use === 'journaling' && t.preference?.includes('journaling'),
      bonus: 15, reason: 'Wide margins designed for journaling'
    },

    // Pastor → experience:mature
    {
      test: (t, a) => a.who === 'pastor' && t.experience?.includes('mature'),
      bonus: 8, reason: 'Trusted by pastors and ministry leaders'
    },

    // Gift → NIV is a well-loved safe choice
    {
      test: (t, a) => (a.use === 'gift' || a.who === 'friend') && t.translation?.includes('niv'),
      bonus: 6, reason: 'A safe and well-loved gift choice'
    },
  ];

  // ── Questions ────────────────────────────────────────────────────

  const QUESTIONS = [
    // Q1 — Who is this for?
    {
      id: 'who',
      question: 'Who is this Bible for?',
      subtitle: "There's no wrong answer — we'll guide you from here.",
      options: [
        { label: 'Myself', desc: 'Finding my own Bible', value: 'me' },
        { label: 'My husband', desc: 'Choosing for my husband', value: 'husband' },
        { label: 'My wife', desc: 'Choosing for my wife', value: 'wife' },
        { label: 'A teenager', desc: 'Age 13–18', value: 'teenager' },
        { label: 'A child', desc: 'Age 12 and under', value: 'child' },
        { label: 'A pastor', desc: 'Ministry & preaching', value: 'pastor' },
        { label: 'A friend', desc: 'A thoughtful gift', value: 'friend' },
      ],
    },

    // Q2 — How will it be used?
    {
      id: 'use',
      question: 'How will it mainly be used?',
      subtitle: 'Choose the one that fits best.',
      options: [
        { label: 'Everyday reading', desc: 'Morning & evening devotion', value: 'everyday-reading' },
        { label: 'Bible study', desc: 'In-depth study & research', value: 'bible-study' },
        { label: 'Church', desc: 'Sunday & church services', value: 'church' },
        { label: 'Preaching', desc: 'Sermon preparation', value: 'preaching' },
        { label: 'Journaling', desc: 'Wide margins for reflection', value: 'journaling' },
        { label: 'Gift', desc: 'A special presentation edition', value: 'gift' },
        { label: 'Travel', desc: 'Compact to take anywhere', value: 'travel' },
      ],
    },

    // Q3 — Language?
    {
      id: 'language',
      question: 'Preferred language?',
      subtitle: 'Want both languages in one Bible? Select Bilingual below.',
      forceGrid: true,
      options: [
        { label: 'English', icon: 'En', isScript: true, value: 'english' },
        { label: 'Bilingual (English + Indian language)', isScript: false, value: 'bilingual' },
        { label: 'Telugu', icon: 'తె', isScript: true, value: 'telugu' },
        { label: 'Hindi', icon: 'हि', isScript: true, value: 'hindi' },
        { label: 'Tamil', icon: 'த', isScript: true, value: 'tamil' },
        { label: 'Malayalam', icon: 'മ', isScript: true, value: 'malayalam' },
        { label: 'Kannada', icon: 'ಕ', isScript: true, value: 'kannada' },
        { label: 'Bengali', icon: 'বাং', isScript: true, value: 'bengali' },
        { label: 'Nepali', icon: 'ने', isScript: true, value: 'nepali' },
        { label: 'Punjabi', icon: 'ਪੰ', isScript: true, value: 'punjabi' },
      ],
    },

    // Q3b — Which Indian language? (bilingual path only)
    {
      id: 'bilingualLang',
      question: 'Which language alongside English?',
      subtitle: "We'll look for Bibles that include both.",
      forceGrid: true,
      onlyIf: () => isBilingualPath(),
      options: [
        { label: 'Telugu', icon: 'తె', isScript: true, value: 'telugu' },
        { label: 'Hindi', icon: 'हि', isScript: true, value: 'hindi' },
        { label: 'Tamil', icon: 'த', isScript: true, value: 'tamil' },
        { label: 'Malayalam', icon: 'മ', isScript: true, value: 'malayalam' },
        { label: 'Kannada', icon: 'ಕ', isScript: true, value: 'kannada' },
        { label: 'Bengali', icon: 'বাং', isScript: true, value: 'bengali' },
        { label: 'Nepali', icon: 'ने', isScript: true, value: 'nepali' },
        { label: 'Punjabi', icon: 'ਪੰ', isScript: true, value: 'punjabi' },
      ],
    },

    // Q4 — Specific translation? (English path only)
    {
      id: 'translation',
      question: 'Do you have a specific translation in mind?',
      subtitle: "Not sure? Pick 'Help me choose' and we'll find the right one for you.",
      onlyIf: () => isEnglishPath(),
      options: [
        { label: 'NIV', desc: 'Most widely read modern Bible', value: 'niv' },
        { label: 'NKJV', desc: 'Classic feel, modern readability', value: 'nkjv' },
        { label: 'NLT', desc: 'Very easy, conversational English', value: 'nlt' },
        { label: 'ESV', desc: 'Word-for-word, trusted for study', value: 'esv' },
        { label: 'KJV', desc: 'The timeless King James Version', value: 'kjv' },
        { label: 'Help me choose', desc: "Match from my reading style preference", value: null },
      ],
    },

    // Q5 — Budget
    {
      id: 'budget',
      question: "What's your budget?",
      subtitle: 'We have beautiful Bibles for every budget.',
      options: [
        { label: 'Under ₹500', value: 'under-500' },
        { label: '₹500 – ₹1,000', value: '500-1000' },
        { label: '₹1,000 – ₹2,500', value: '1000-2500' },
        { label: '₹2,500 and above', value: '2500-plus' },
        { label: 'No budget limit', value: 'any' },
      ],
    },

    // Q6 — Print size
    {
      id: 'printSize',
      question: 'What print size do you prefer?',
      subtitle: 'Larger print is easier on the eyes for extended reading.',
      options: [
        { label: 'Standard print', desc: 'Regular size text', value: 'standard' },
        { label: 'Large print', desc: 'Noticeably bigger letters', value: 'large' },
        { label: 'Giant print', desc: 'Maximum readability', value: 'giant' },
        { label: 'No preference', desc: 'Any size works for me', value: null },
      ],
    },

    // Q7 — Format / cover
    {
      id: 'format',
      question: 'Any preference for size or cover?',
      subtitle: 'This helps us narrow it down further.',
      options: [
        { label: 'Thinline / slim', desc: 'Sleek and lightweight', value: 'thinline' },
        { label: 'Compact / pocket', desc: 'Fits easily in a bag', value: 'compact' },
        { label: 'Leather or leather-look', desc: 'Premium feel and durability', value: 'leather' },
        { label: 'No preference', desc: 'Any size or cover works', value: null },
      ],
    },
  ];

  // ── State ────────────────────────────────────────────────────────
  let answers = [];
  let catalog = [];

  // ── Path detection ───────────────────────────────────────────────
  // Language is always Q3 (index 2). answers[2] holds the language pick.
  function isEnglishPath() {
    return !answers[2] || answers[2].value === 'english' || answers[2].value === 'bilingual';
  }

  function isBilingualPath() {
    return answers[2]?.value === 'bilingual';
  }

  function getActiveQuestions() {
    return QUESTIONS.filter(q => !q.onlyIf || q.onlyIf());
  }

  function buildAnswerMap() {
    const active = getActiveQuestions();
    const map = {};
    active.forEach((q, i) => {
      if (answers[i] !== undefined) map[q.id] = answers[i].value;
    });
    return map;
  }

  // ── Answer derivation helpers ────────────────────────────────────
  // These translate human answers (who + use) into tag-level concepts.

  const INDIAN_LANGS = ['telugu', 'hindi', 'tamil', 'malayalam', 'bengali', 'nepali',
    'kannada', 'punjabi', 'assamese', 'odia', 'marathi', 'gujarati', 'urdu'];

  function getEffectiveExperience(a) {
    if (a.who === 'child') return 'kids';
    if (a.who === 'teenager') return 'youth';
    if (a.who === 'pastor') return 'mature';
    if (a.who === 'friend' || a.use === 'gift') return 'gift';
    // me / husband / wife → infer from use
    if (a.use === 'bible-study' || a.use === 'preaching') return 'mature';
    return 'new';
  }

  function getEffectivePurpose(a) {
    if (a.who === 'child') return 'kids';
    if (a.who === 'teenager') return 'youth';
    const MAP = {
      'everyday-reading': 'daily-reading',
      'bible-study': 'study',
      'church': 'church',
      'preaching': 'study',
      'journaling': 'daily-reading',
      'gift': 'daily-reading',
      'travel': 'church',
    };
    return MAP[a.use] || 'daily-reading';
  }

  function getEffectivePreference(a) {
    if (a.use === 'journaling') return 'journaling';
    if (a.use === 'travel') return 'compact';
    if (a.use === 'church') return 'thinline';
    return null;
  }

  // ── Physical attribute detectors (title + tag based) ────────────────
  function hasLargePrint(bible) {
    return /\b(large[\s-]?print|giant[\s-]?print|big[\s-]?print)\b/i.test(bible.title) ||
      bible.tags.preference?.includes('large-print') ||
      bible.tags['print-size']?.some(v => ['large', 'giant', 'lp'].includes(v));
  }

  function hasLeatherBinding(bible) {
    return /\b(leather(?:soft)?|bonded[\s-]leather|genuine[\s-]leather|imitation[\s-]leather|leather[\s-]?look)\b/i.test(bible.title) ||
      bible.tags.binding?.includes('premium') ||
      bible.tags.binding?.some(v => v.includes('leather'));
  }

  function hasThinlineFormat(bible) {
    return /\b(thinline|thin[\s-]line|slim)\b/i.test(bible.title) ||
      bible.tags.preference?.includes('thinline');
  }

  function hasCompactFormat(bible) {
    return /\b(compact|pocket|mini)\b/i.test(bible.title) ||
      bible.tags.preference?.includes('compact');
  }

  // ── Budget helpers ───────────────────────────────────────────────
  function getBudgetRange(budgetVal) {
    const MAP = {
      'under-500': { min: 0, max: 500 },
      '500-1000': { min: 500, max: 1000 },
      '1000-2500': { min: 1000, max: 2500 },
      '2500-plus': { min: 2500, max: Infinity },
    };
    return MAP[budgetVal] || { min: 0, max: Infinity };
  }

  function scoreBudget(price, budgetVal) {
    const { min, max } = getBudgetRange(budgetVal);
    if (min === 0 && max === Infinity) return 1;       // no limit
    if (price >= min && price <= max) return 1;       // perfect fit
    if (min > 0 && price < min) return 0.8;     // under budget is fine
    if (max !== Infinity && price <= max * 1.12) return 0.4; // just over
    return 0;
  }

  // ── Confidence label ─────────────────────────────────────────────
  function getConfidence(score) {
    if (score >= 90) return { label: 'Excellent Match', cls: 'br__badge--excellent' };
    if (score >= 75) return { label: 'Great Match', cls: 'br__badge--great' };
    if (score >= 60) return { label: 'Good Match', cls: 'br__badge--good' };
    if (score >= CONFIG.minScore) return { label: 'Possible Match', cls: 'br__badge--possible' };
    return null;
  }

  // ── Personality derive ───────────────────────────────────────────
  function getPersonality(a) {
    if (a.who === 'child') {
      return { type: 'Little Explorer', desc: 'A Bible that makes Scripture come alive for young hearts.' };
    }
    if (a.who === 'teenager') {
      return { type: 'Young Seeker', desc: 'A Bible that speaks to where they are right now.' };
    }
    if (a.who === 'pastor') {
      return { type: 'Servant Leader', desc: 'A Bible worthy of their calling and ministry.' };
    }
    if (a.who === 'friend' || a.use === 'gift') {
      return { type: 'Generous Giver', desc: "You're choosing something they'll treasure for years." };
    }
    const byUse = {
      'everyday-reading': { type: 'Daily Reader', desc: 'A Bible you\'ll open every single morning.' },
      'bible-study': { type: 'Diligent Student', desc: 'You love going deep into what the Word really means.' },
      'church': { type: 'Faithful Worshipper', desc: 'Your Bible goes wherever worship takes you.' },
      'preaching': { type: 'Faithful Preacher', desc: 'Every word matters when you\'re at the pulpit.' },
      'journaling': { type: 'Reflective Soul', desc: 'You read, reflect, and write — your Bible is your companion.' },
      'travel': { type: 'On-the-Go Believer', desc: 'Your faith travels with you, and so does your Bible.' },
    };
    return byUse[a.use] || { type: 'Devoted Reader', desc: 'We\'ve found the perfect Bible for your journey.' };
  }

  // ── Step label with encouragement ───────────────────────────────
  function getStepLabel(stepIndex, total) {
    let hint = '';
    if (stepIndex === total - 1) hint = 'Last one!';
    else if (stepIndex === total - 2) hint = 'Almost done';
    else if (stepIndex > 0 && stepIndex === Math.floor(total / 2)) hint = 'Halfway there';
    else if (stepIndex > 0) hint = 'Great start!';
    return `Step ${stepIndex + 1} of ${total}${hint ? ` · ${hint}` : ''}`;
  }

  // ── Answer summary bullets ───────────────────────────────────────
  function buildSummaryPoints(a) {
    const points = [];
    const LANG_NAME = {
      telugu: 'Telugu', hindi: 'Hindi', tamil: 'Tamil', malayalam: 'Malayalam',
      kannada: 'Kannada', bengali: 'Bengali', nepali: 'Nepali', punjabi: 'Punjabi'
    };
    if (a.language === 'bilingual' && a.bilingualLang) {
      const ln = LANG_NAME[a.bilingualLang] || a.bilingualLang;
      points.push(`English + ${ln} bilingual Bible`);
    } else if (a.language && a.language !== 'english') {
      points.push((LANG_NAME[a.language] || a.language) + ' Bible');
    } else {
      points.push(a.translation ? (a.translation.toUpperCase() + ' Bible') : 'English Bible');
    }
    const USE = {
      'everyday-reading': 'Great for daily devotions',
      'bible-study': 'Designed for in-depth study',
      'church': 'Easy to carry to church',
      'preaching': 'Built for preaching & ministry',
      'journaling': 'Wide margins for journaling',
      'gift': 'A beautiful gift edition',
      'travel': 'Compact to take anywhere',
    };
    if (a.use) points.push(USE[a.use] || '');
    const PRINT = { standard: 'Standard print', large: 'Large print edition', giant: 'Giant print edition' };
    if (a.printSize) points.push(PRINT[a.printSize]);
    const FORMAT = { thinline: 'Slim thinline design', compact: 'Compact / pocket size', leather: 'Leather or leather-look cover' };
    if (a.format) points.push(FORMAT[a.format]);
    const BUDGET = {
      'under-500': 'Under ₹500', '500-1000': '₹500 – ₹1,000',
      '1000-2500': '₹1,000 – ₹2,500', '2500-plus': 'Premium edition'
    };
    if (a.budget && a.budget !== 'any') points.push(BUDGET[a.budget]);
    const WHO = {
      child: 'Perfect for children', teenager: 'Great for teens',
      pastor: 'For ministry & preaching'
    };
    if (a.who && WHO[a.who]) points.push(WHO[a.who]);
    return points.filter(Boolean).slice(0, 4);
  }

  // ── Layout class ─────────────────────────────────────────────────

  // ── Tag parsing ──────────────────────────────────────────────────
  function parseTags(shopifyTags) {
    const tags = {};
    const list = Array.isArray(shopifyTags)
      ? shopifyTags
      : String(shopifyTags || '').split(',').map(s => s.trim()).filter(Boolean);
    list.forEach(tag => {
      const idx = tag.indexOf(':');
      if (idx > 0) {
        const k = tag.slice(0, idx).trim().toLowerCase();
        const v = tag.slice(idx + 1).trim().toLowerCase();
        if (!tags[k]) tags[k] = [];
        tags[k].push(v);
      }
    });
    return tags;
  }

  function parseProduct(p) {
    const price = parseFloat(p.variants?.[0]?.price || p.price || '0') || 0;
    // A product is in stock if at least one variant is available
    const available = p.variants
      ? p.variants.some(v => v.available)
      : (p.available !== false);
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      price,
      available,
      image: p.images?.[0]?.src || p.image || null,
      tags: parseTags(p.tags),
    };
  }

  // ── English scoring ──────────────────────────────────────────────
  function scoreEnglish(bible, a) {
    const t = bible.tags;
    let score = 0;
    const reasons = [];

    // Skip Indian-language Bibles on the English path
    if (INDIAN_LANGS.some(l => t.language?.includes(l))) {
      return { score: 0, reasons: [] };
    }

    // Safety net for products missing a language tag:
    // filter by language name in the title or non-Latin script characters
    const titleForLangCheck = bible.title.toLowerCase();
    if (INDIAN_LANGS.some(l => titleForLangCheck.includes(l))) {
      return { score: 0, reasons: [] };
    }
    // Arabic/Urdu (0600–06FF) + Brahmic scripts: Devanagari→Malayalam (0900–0D7F)
    if (/[؀-ۿऀ-ൿ]/.test(bible.title)) {
      return { score: 0, reasons: [] };
    }

    // Products with no language tag at all are non-Bible items (wall art, décor, etc.)
    if (!t.language || t.language.length === 0) {
      return { score: 0, reasons: [] };
    }

    const exp = getEffectiveExperience(a);
    const purp = getEffectivePurpose(a);
    const pref = getEffectivePreference(a);

    // ── Purpose (35 pts) ────────────────────────────────────────
    const PURP_TAG = {
      'daily-reading': 'daily-reading', 'study': 'study',
      'church': 'church', 'kids': 'kids', 'youth': 'youth'
    };
    const PURP_REASON = {
      'daily-reading': 'Great for everyday devotional reading',
      'study': 'Designed for in-depth Bible study',
      'church': 'Compact and easy to carry to church',
      'kids': 'Designed especially for children',
      'youth': 'Tailored for teens and young adults',
    };
    const pTag = PURP_TAG[purp];
    if (pTag && t.purpose?.includes(pTag)) {
      score += W.purpose;
      if (PURP_REASON[purp]) reasons.push(PURP_REASON[purp]);
    } else if (!t.purpose) {
      score += W.purpose * 0.4;   // untagged → partial
    }

    // ── Experience (25 pts) ──────────────────────────────────────
    const EXP_TAG = {
      new: 'new', growing: 'growing', mature: 'mature',
      kids: 'kids', youth: 'youth', gift: 'gift'
    };
    const EXP_REASON = {
      new: 'A great first Bible',
      mature: 'Rich content for mature believers',
      kids: 'Designed for children',
      youth: 'Engaging for teens and youth',
      gift: 'Makes a beautiful and lasting gift',
    };
    const eTag = EXP_TAG[exp];
    if (eTag && t.experience?.includes(eTag)) {
      score += W.experience;
      if (EXP_REASON[exp]) reasons.push(EXP_REASON[exp]);
    } else if (t.experience?.includes('growing') && ['new', 'growing'].includes(exp)) {
      score += W.experience * 0.7;
    } else if (!t.experience) {
      score += W.experience * 0.5;
    }

    // ── Reading style → translation (25 pts) ────────────────────
    // a.translation is an explicit user pick (e.g. 'esv') that overrides style-derived matching.
    const styleVal = a.style;
    const translationOverride = a.translation;   // null means "help me choose"

    if (!styleVal && !translationOverride) {
      score += W.style;   // no preference at all → full marks, don't penalise
    } else {
      const matching = translationOverride
        ? [translationOverride]
        : (STYLE_TRANSLATIONS[styleVal] || []);

      if (matching.length > 0 && matching.some(tr => t.translation?.includes(tr))) {
        score += W.style;
        if (translationOverride) {
          reasons.push(translationOverride.toUpperCase() + ' translation');
        } else {
          const STYLE_REASON = {
            'very-easy': 'Easy to read and understand',
            'modern': 'Clear, modern English',
            'traditional': 'Classic, traditional translation',
          };
          if (STYLE_REASON[styleVal]) reasons.push(STYLE_REASON[styleVal]);
        }
      } else if (translationOverride) {
        // User explicitly named a translation — heavily penalise anything that doesn't match
        // so no print/format bonus can push a wrong-translation Bible above a correct one.
        score -= 50;
      }
      // Wrong style (no explicit override) = 0 pts
    }

    // ── Preference bonus (+8 on top of base) ────────────────────
    if (pref && t.preference?.includes(pref)) {
      score += 8;
      const PREF_REASON = {
        journaling: 'Wide margins for notes and journaling',
        compact: 'Compact size, easy to carry',
        thinline: 'Slim and lightweight',
      };
      if (PREF_REASON[pref]) reasons.push(PREF_REASON[pref]);
    } else if (pref === 'journaling') {
      // Journaling is a very specific product type — penalise hard enough to drop any
      // non-journaling Bible below minScore (40) even when it scores well on other axes.
      // Max non-journaling score before this line is ~100; −65 ensures result < 40.
      score -= 65;
    }

    // ── Budget (15 pts) ──────────────────────────────────────────
    const bScore = scoreBudget(bible.price, a.budget);
    score += W.budget * bScore;
    if (bScore >= 1 && a.budget !== 'any') reasons.push('Fits your budget');

    // ── Expert boosts ────────────────────────────────────────────
    for (const rule of EXPERT_BOOSTS) {
      if (rule.test(t, a, bible.price)) {
        score += rule.bonus;
        if (rule.reason) reasons.push(rule.reason);
      }
    }

    // ── Print size (15 pts bonus / -20 penalty) ──────────────────
    if (a.printSize) {
      const isLarge = hasLargePrint(bible);
      if (a.printSize === 'large' || a.printSize === 'giant') {
        if (isLarge) {
          score += 15;
          reasons.push('Large print for comfortable reading');
        } else {
          score -= 20;
        }
      } else if (a.printSize === 'standard' && isLarge) {
        score -= 8;
      }
    }

    // ── Format / cover (10 pts bonus) ────────────────────────────
    if (a.format) {
      if (a.format === 'thinline') {
        if (hasThinlineFormat(bible)) { score += 10; reasons.push('Slim and lightweight'); }
        else if (hasCompactFormat(bible)) score += 5;
      } else if (a.format === 'compact') {
        if (hasCompactFormat(bible)) { score += 10; reasons.push('Compact size, easy to carry'); }
        else if (hasThinlineFormat(bible)) score += 5;
      } else if (a.format === 'leather') {
        if (hasLeatherBinding(bible)) { score += 10; reasons.push('Beautiful leather binding'); }
      }
    }

    // ── Penalties ────────────────────────────────────────────────
    if (a.who === 'child' && bible.price > 1500) score -= 20;
    if (exp === 'new' && bible.price > 5000) score -= 30;

    // Pocket/compact Bibles are not well-suited for everyday reading (better for travel/church)
    if (a.use === 'everyday-reading') {
      const isPocket = t.preference?.includes('compact') || /\bpocket\b/.test(bible.title.toLowerCase());
      if (isPocket) score -= 22;
    }

    // Never recommend a kids/youth Bible to an adult, or adult Bible to a child
    // Also detect youth/kids directly from title for products the tagger missed
    const titleLower = bible.title.toLowerCase();
    const titleIsKids = /\b(kids?|children|child|adventure bible|early reader|baby|babies|infant|toddler)\b/.test(titleLower);
    const titleIsYouth = /\b(boys?|girls?|teen|youth|young adult|student)\b/.test(titleLower);

    const isKidsProduct = t.experience?.includes('kids') || t.purpose?.includes('kids') || titleIsKids;
    const isYouthProduct = t.experience?.includes('youth') || t.purpose?.includes('youth') || titleIsYouth;
    const buyingForAdult = !['child', 'teenager'].includes(a.who);
    const buyingForChild = a.who === 'child';

    if (isKidsProduct && !buyingForChild) score -= 50;
    if (isYouthProduct && buyingForChild) score -= 30;
    if (isYouthProduct && buyingForAdult && a.who !== 'friend') score -= 40;
    if (!isKidsProduct && !isYouthProduct && buyingForChild) score -= 20;

    // Hard cap if clearly over budget
    const { max } = getBudgetRange(a.budget);
    if (max !== Infinity && bible.price > max * 1.15) {
      score = Math.min(score, CONFIG.minScore - 1);
    }

    return {
      score: Math.max(0, Math.min(100, Math.round(score))),
      reasons: [...new Set(reasons)].slice(0, 3),
    };
  }

  // ── Indian scoring ───────────────────────────────────────────────
  function scoreIndian(bible, a) {
    const t = bible.tags;
    let score = 0;
    const reasons = [];

    // ── Language (40 pts — near hard filter) ────────────────────
    const langVal = a.language;
    if (langVal && INDIAN_LANGS.includes(langVal)) {
      if (t.language?.includes(langVal)) {
        score += W_IN.language;
        reasons.push(`Available in ${langVal.charAt(0).toUpperCase() + langVal.slice(1)}`);
      } else {
        return { score: 0, reasons: [] };   // wrong language → irrelevant
      }
    } else {
      // No specific language picked — show any Indian-language Bible
      if (!INDIAN_LANGS.some(l => t.language?.includes(l))) {
        return { score: 0, reasons: [] };
      }
      score += W_IN.language;
    }

    // ── Use → special/purpose (30 pts) ──────────────────────────
    const useVal = a.use;
    const whoVal = a.who;

    let specialMatch = false;
    let useReason = '';

    if (whoVal === 'child') {
      specialMatch = t.special?.includes('kids');
      useReason = 'Designed for children';
    } else if (useVal === 'bible-study' || useVal === 'preaching') {
      specialMatch = t.special?.includes('study');
      useReason = useVal === 'preaching' ? 'Good for sermon preparation' : 'Includes study notes';
    } else {
      // Standard everyday reading = no special tag
      specialMatch = !t.special || t.special.length === 0;
      const USE_REASON = {
        'everyday-reading': 'Good for everyday reading',
        'church': 'Easy to carry to church',
        'travel': 'Compact and portable',
        'journaling': 'Good for reading and reflection',
        'gift': 'Makes a thoughtful gift',
      };
      useReason = USE_REASON[useVal] || '';
    }

    if (specialMatch) {
      score += W_IN.use;
      if (useReason) reasons.push(useReason);
    }

    // ── Who → experience (15 pts) ───────────────────────────────
    const exp = getEffectiveExperience(a);
    const eTag = {
      new: 'new', growing: 'growing', mature: 'mature',
      kids: 'kids', youth: 'youth', gift: 'gift'
    }[exp];
    if (eTag && t.experience?.includes(eTag)) {
      score += W_IN.who;
    } else if (!t.experience) {
      score += W_IN.who * 0.5;
    }

    // ── Budget (15 pts) ──────────────────────────────────────────
    const bScore = scoreBudget(bible.price, a.budget);
    score += W_IN.budget * bScore;
    if (bScore >= 1 && a.budget !== 'any') reasons.push('Fits your budget');

    // Never recommend a kids/youth Bible to the wrong buyer
    const titleLower = bible.title.toLowerCase();
    const titleIsKids = /\b(kids?|children|child|adventure bible|early reader|baby|babies|infant|toddler)\b/.test(titleLower);
    const titleIsYouth = /\b(boys?|girls?|teen|youth|young adult|student)\b/.test(titleLower);

    const isKidsProduct = t.special?.includes('kids') || t.experience?.includes('kids') || titleIsKids;
    const isYouthProduct = t.special?.includes('youth') || t.experience?.includes('youth') || titleIsYouth;
    const buyingForAdult = !['child', 'teenager'].includes(a.who);
    const buyingForChild = a.who === 'child';

    if (isKidsProduct && !buyingForChild) score -= 50;
    if (isYouthProduct && buyingForChild) score -= 30;
    if (isYouthProduct && buyingForAdult && a.who !== 'friend') score -= 40;
    if (!isKidsProduct && !isYouthProduct && buyingForChild) score -= 20;

    // ── Print size (15 pts bonus / -20 penalty) ──────────────────
    if (a.printSize) {
      const isLarge = hasLargePrint(bible);
      if (a.printSize === 'large' || a.printSize === 'giant') {
        if (isLarge) {
          score += 15;
          reasons.push('Large print for comfortable reading');
        } else {
          score -= 20;
        }
      } else if (a.printSize === 'standard' && isLarge) {
        score -= 8;
      }
    }

    // ── Format / cover (10 pts bonus) ────────────────────────────
    if (a.format) {
      if (a.format === 'thinline') {
        if (hasThinlineFormat(bible)) { score += 10; reasons.push('Slim and lightweight'); }
        else if (hasCompactFormat(bible)) score += 5;
      } else if (a.format === 'compact') {
        if (hasCompactFormat(bible)) { score += 10; reasons.push('Compact size, easy to carry'); }
        else if (hasThinlineFormat(bible)) score += 5;
      } else if (a.format === 'leather') {
        if (hasLeatherBinding(bible)) { score += 10; reasons.push('Beautiful leather binding'); }
      }
    }

    // Hard cap if over budget
    const { max } = getBudgetRange(a.budget);
    if (max !== Infinity && bible.price > max * 1.15) {
      score = Math.min(score, CONFIG.minScore - 1);
    }

    return {
      score: Math.max(0, Math.min(100, Math.round(score))),
      reasons: [...new Set(reasons)].slice(0, 3),
    };
  }

  // ── Bilingual scoring ─────────────────────────────────────────────
  // Weights: language(30) + bilingual-confirmed(25) + purpose(20) + style(15) + budget(10) = 100
  function scoreBilingual(bible, a) {
    const t = bible.tags;
    let score = 0;
    const reasons = [];

    const bilingualLang = a.bilingualLang;
    if (!bilingualLang) return { score: 0, reasons: [] };

    // Must have the selected Indian language
    if (!t.language?.includes(bilingualLang)) return { score: 0, reasons: [] };
    score += 30;

    // Reward confirmed bilingual products: language:english tag, bilingual tag, or
    // an English translation abbreviation in the title (Indian-only Bibles never have these).
    const titleLower = bible.title.toLowerCase();
    const isBilingual = t.language?.includes('english') ||
      t.language?.some(l => l.startsWith('bilingual')) ||
      t.bilingual?.length > 0 ||
      /\bbilingual\b/.test(titleLower) ||
      /\bdiglot\b/.test(titleLower) ||
      /\b(esv|niv|kjv|nkjv|nlt|nasb|nirv|gnt|icb|net)\b/.test(titleLower);
    const langName = bilingualLang.charAt(0).toUpperCase() + bilingualLang.slice(1);
    if (isBilingual) {
      score += 25;
      reasons.push(`Available in English and ${langName}`);
    } else {
      reasons.push(`Available in ${langName}`);
    }

    // Purpose (20 pts)
    const purp = getEffectivePurpose(a);
    const PURP_TAG = {
      'daily-reading': 'daily-reading', 'study': 'study',
      'church': 'church', 'kids': 'kids', 'youth': 'youth'
    };
    const pTag = PURP_TAG[purp];
    if (pTag && t.purpose?.includes(pTag)) {
      score += 20;
    } else if (!t.purpose) {
      score += 20 * 0.5;
    }

    // Style → translation (15 pts) — matches the English translation half of the bilingual Bible
    const styleVal = a.style;
    if (!styleVal) {
      score += 15;
    } else {
      const matching = STYLE_TRANSLATIONS[styleVal] || [];
      const tagMatch = matching.some(tr => t.translation?.includes(tr));
      const titleMatch = matching.some(tr => titleLower.includes(tr));
      if (tagMatch || titleMatch) {
        score += 15;
        const STYLE_REASON = { 'very-easy': 'Easy to read', 'modern': 'Clear modern English', 'traditional': 'Classic English translation' };
        if (STYLE_REASON[styleVal]) reasons.push(STYLE_REASON[styleVal]);
      }
    }

    // Budget (10 pts)
    const bScore = scoreBudget(bible.price, a.budget);
    score += 10 * bScore;
    if (bScore >= 1 && a.budget !== 'any') reasons.push('Fits your budget');

    // Kids/youth safety checks
    const titleIsKids = /\b(kids?|children|child|adventure bible|early reader|baby|babies|infant|toddler)\b/.test(titleLower);
    const titleIsYouth = /\b(boys?|girls?|teen|youth|young adult|student)\b/.test(titleLower);
    const isKidsProduct = t.special?.includes('kids') || t.experience?.includes('kids') || titleIsKids;
    const isYouthProduct = t.special?.includes('youth') || t.experience?.includes('youth') || titleIsYouth;
    const buyingForChild = a.who === 'child';
    const buyingForAdult = !['child', 'teenager'].includes(a.who);
    if (isKidsProduct && !buyingForChild) score -= 50;
    if (isYouthProduct && buyingForChild) score -= 30;
    if (isYouthProduct && buyingForAdult && a.who !== 'friend') score -= 40;

    // ── Print size (15 pts bonus / -20 penalty) ──────────────────
    if (a.printSize) {
      const isLarge = hasLargePrint(bible);
      if (a.printSize === 'large' || a.printSize === 'giant') {
        if (isLarge) {
          score += 15;
          reasons.push('Large print for comfortable reading');
        } else {
          score -= 20;
        }
      } else if (a.printSize === 'standard' && isLarge) {
        score -= 8;
      }
    }

    // ── Format / cover (10 pts bonus) ────────────────────────────
    if (a.format) {
      if (a.format === 'thinline') {
        if (hasThinlineFormat(bible)) { score += 10; reasons.push('Slim and lightweight'); }
        else if (hasCompactFormat(bible)) score += 5;
      } else if (a.format === 'compact') {
        if (hasCompactFormat(bible)) { score += 10; reasons.push('Compact size, easy to carry'); }
        else if (hasThinlineFormat(bible)) score += 5;
      } else if (a.format === 'leather') {
        if (hasLeatherBinding(bible)) { score += 10; reasons.push('Beautiful leather binding'); }
      }
    }

    const { max } = getBudgetRange(a.budget);
    if (max !== Infinity && bible.price > max * 1.15) {
      score = Math.min(score, CONFIG.minScore - 1);
    }

    return {
      score: Math.max(0, Math.min(100, Math.round(score))),
      reasons: [...new Set(reasons)].slice(0, 3),
    };
  }

  // ── Match & rank ─────────────────────────────────────────────────
  function findMatches() {
    const a = buildAnswerMap();
    const scoreFn = a.language === 'bilingual' ? scoreBilingual
      : (a.language === 'english' || !a.language) ? scoreEnglish
        : scoreIndian;

    const scored = catalog
      .filter(b => b.available)          // never recommend sold-out products
      .map(b => ({ ...b, ...scoreFn(b, a) }))
      .sort((x, y) => y.score - x.score);

    const above = scored.filter(b => b.score >= CONFIG.minScore);
    if (above.length > 0) {
      // Return top 3 to display + up to 7 more as AI alternatives
      return {
        results: above.slice(0, CONFIG.maxResults),
        alternatives: above.slice(CONFIG.maxResults, CONFIG.maxResults + 7),
        usedFallback: false,
      };
    }
    return {
      results: scored.slice(0, CONFIG.maxResults),
      alternatives: scored.slice(CONFIG.maxResults, CONFIG.maxResults + 7),
      usedFallback: true,
    };
  }

  // ── Fetch from Shopify ───────────────────────────────────────────
  async function fetchCatalog() {
    if (window.BR_SAMPLE_DATA) {
      return window.BR_SAMPLE_DATA.map(parseProduct);
    }
    const base = `/collections/${CONFIG.collectionHandle}/products.json?limit=250`;
    const [p1, p2] = await Promise.all([
      fetch(`${base}&page=1`).then(r => r.json()),
      fetch(`${base}&page=2`).then(r => r.json()),
    ]);
    const products = [...(p1.products || []), ...(p2.products || [])];
    if (!products.length) throw new Error('No products returned.');
    return products.map(parseProduct);
  }

  // ── Tracking ─────────────────────────────────────────────────────
  function trackResponse(results, usedFallback) {
    console.log('[BR Track] trackingUrl =', CONFIG.trackingUrl);
    if (!CONFIG.trackingUrl) { console.warn('[BR Track] No URL — skipping'); return; }
    const a = buildAnswerMap();
    const personality = getPersonality(a);
    const payload = {
      timestamp: Date.now(),
      who: a.who || '',
      use: a.use || '',
      language: a.language || '',
      bilingualLang: a.bilingualLang || '',
      style: a.style || '',
      translation: a.translation || '',
      budget: a.budget || '',
      printSize: a.printSize || '',
      format: a.format || '',
      personality: personality.type,
      results: results.map(b => ({ handle: b.handle, title: b.title.slice(0, 55), score: b.score, price: b.price })),
      usedFallback,
    };
    // Use an Image ping — bypasses Shopify's connect-src CSP restriction
    const url = CONFIG.trackingUrl + '?data=' + encodeURIComponent(JSON.stringify(payload));
    console.log('[BR Track] Sending ping to:', url.slice(0, 80) + '...');
    const img = new Image();
    img.onload = () => console.log('[BR Track] Ping succeeded');
    img.onerror = (e) => console.warn('[BR Track] Ping failed (normal for text responses):', e.type);
    img.src = url;
  }

  // ── Generic event tracking ────────────────────────────────────────
  // Sends lightweight funnel events (quiz_started, step_answered, product_clicked).
  // Uses the same Image-ping technique as trackResponse to bypass Shopify's CSP.
  function trackEvent(type, extra) {
    if (!CONFIG.trackingUrl) return;
    const a = buildAnswerMap();
    const payload = Object.assign({
      type,
      timestamp: Date.now(),
      who: a.who || '',
      use: a.use || '',
      language: a.language || '',
      bilingualLang: a.bilingualLang || '',
      style: a.style || '',
      translation: a.translation || '',
      budget: a.budget || '',
      printSize: a.printSize || '',
      format: a.format || '',
    }, extra || {});
    const img = new Image();
    img.src = CONFIG.trackingUrl + '?data=' + encodeURIComponent(JSON.stringify(payload));
  }

  // ── Chat tracking ─────────────────────────────────────────────────
  function trackChat(question, aiResponse) {
    if (!CONFIG.trackingUrl) return;
    const a = buildAnswerMap();
    const payload = {
      type: 'chat',
      timestamp: Date.now(),
      who: a.who || '',
      use: a.use || '',
      language: a.language || '',
      bilingualLang: a.bilingualLang || '',
      style: a.style || '',
      translation: a.translation || '',
      budget: a.budget || '',
      printSize: a.printSize || '',
      format: a.format || '',
      question,
      aiReply: (aiResponse.reply || '').slice(0, 300),
      needs: (aiResponse.needs || []).join(' | '),
      whyChanged: aiResponse.whyChanged || '',
      alternativeHandle: aiResponse.alternativeHandle || '',
    };
    const img = new Image();
    img.src = CONFIG.trackingUrl + '?data=' + encodeURIComponent(JSON.stringify(payload));
  }

  // ── Welcome screen ────────────────────────────────────────────────
  function showWelcome() {
    const area = document.getElementById('br-question-area');
    area.classList.remove('is-exiting');
    document.getElementById('br-progress').style.width = '0%';
    area.innerHTML = `
      <div class="br__welcome">
        <div class="br__welcome-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
          </svg>
        </div>
        <h2 class="br__welcome-heading">Let's find your perfect Bible</h2>
        <p class="br__welcome-sub">Answer a few quick questions.<br>It takes less than a minute.</p>
        ${CONFIG.socialProof ? `<p class="br__welcome-social">🙏 ${CONFIG.socialProof}+ people found their Bible using this</p>` : ''}
        <button class="br__welcome-btn" id="br-start" type="button">Let's begin →</button>
      </div>
    `;
    document.getElementById('br-start').addEventListener('click', () => {
      trackEvent('quiz_started');
      area.classList.add('is-exiting');
      setTimeout(() => showStep(0), 220);
    });
  }

  // ── Init ─────────────────────────────────────────────────────────
  function init() {
    const container = document.getElementById('bible-recommender');
    if (!container) return;
    renderShell(container);
    showWelcome();
  }

  // ── Shell ─────────────────────────────────────────────────────────
  function renderShell(container) {
    container.innerHTML = `
      <div class="br">
        <div class="br__progress">
          <div class="br__progress-bar" id="br-progress"></div>
        </div>
        <div id="br-question-area"></div>
        <div class="br__loading" id="br-loading">
          <div class="br__wave">
            <span></span><span></span><span></span><span></span><span></span>
            <span></span><span></span><span></span><span></span>
          </div>
          <p>Finding your perfect Bible…</p>
        </div>
        <div class="br__results" id="br-results">
          <div class="br__fallback-notice hidden" id="br-fallback-notice">
            We couldn't find a perfect match — here are the closest options from our collection.
          </div>
          <div id="br-results-content"></div>
          <div class="br__results-footer">
            <button class="br__restart" id="br-restart" type="button">← Start over</button>
            <a href="/collections/all-products" class="br__browse-all">Browse all Bibles →</a>
          </div>
        </div>
      </div>
    `;
    document.getElementById('br-restart').addEventListener('click', restart);
  }

  // ── Render a step ─────────────────────────────────────────────────
  function showStep(stepIndex) {
    const questions = getActiveQuestions();
    const q = questions[stepIndex];
    const isLastStep = stepIndex === questions.length - 1;
    const savedAnswer = answers[stepIndex];
    const isLang = !!q.forceGrid;
    const optionsClass = isLang ? 'br__options br__options--lang' : 'br__options';

    document.getElementById('br-progress').style.width =
      ((stepIndex / questions.length) * 100) + '%';

    const backSvg = `<svg viewBox="0 0 16 16" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 12L6 8l4-4"/></svg>`;

    const area = document.getElementById('br-question-area');
    area.classList.remove('is-exiting');
    area.innerHTML = `
      <div class="br__step">
        <div class="br__top-bar">
          ${stepIndex > 0
        ? `<button class="br__back" id="br-back" type="button">${backSvg} Back</button>`
        : '<span></span>'}
          <span class="br__counter">${getStepLabel(stepIndex, questions.length)}</span>
        </div>
        <h2 class="br__question">${q.question}</h2>
        ${q.subtitle ? `<p class="br__subtitle">${q.subtitle}</p>` : ''}
        <div class="${optionsClass}">
          ${q.options.map((opt, i) => {
          const isSel = savedAnswer?.optionIndex === i;
          if (isLang && opt.isScript) {
            return `
                <button class="br__option${isSel ? ' is-selected' : ''}" data-index="${i}" type="button">
                  <span class="br__lang-script">${opt.icon}</span>
                  <span class="br__opt-label">${opt.label}</span>
                </button>`;
          }
          if (isLang) {
            return `
                <button class="br__option${isSel ? ' is-selected' : ''}" data-index="${i}" type="button"
                        style="grid-column: 1 / -1; justify-content: center; min-height: 44px;">
                  <span class="br__opt-label">${opt.label}</span>
                </button>`;
          }
          return `
              <button class="br__option${isSel ? ' is-selected' : ''}" data-index="${i}" type="button">
                <span class="br__opt-label">${opt.label}</span>
                ${opt.desc ? `<span class="br__opt-desc">${opt.desc}</span>` : ''}
              </button>`;
        }).join('')}
        </div>
        ${isLastStep ? `
          <div class="br__submit-wrap">
            <button class="br__submit" id="br-submit" type="button"
                    ${savedAnswer === undefined ? 'disabled' : ''}>
              Find my Bible →
            </button>
          </div>` : ''}
      </div>
    `;

    // ── Option click: auto-advance ────────────────────────────────
    let advancing = false;

    area.querySelectorAll('.br__option').forEach((btn, i) => {
      btn.addEventListener('click', () => {
        if (advancing) return;

        area.querySelectorAll('.br__option').forEach(b => b.classList.remove('is-selected', 'is-advancing'));
        btn.classList.add('is-selected', 'is-advancing');

        if (q.id === 'language') answers = answers.slice(0, stepIndex);
        answers[stepIndex] = { value: q.options[i].value, optionIndex: i };

        trackEvent('step_answered', { step: q.id, answer: q.options[i].value });

        const submitBtn = document.getElementById('br-submit');
        if (submitBtn) submitBtn.disabled = false;

        if (isLastStep) {
          btn.classList.remove('is-advancing');
          return;
        }

        advancing = true;
        area.classList.add('is-exiting');
        setTimeout(() => showStep(stepIndex + 1), 240);
      });
    });

    const submitBtn = document.getElementById('br-submit');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => {
        if (answers[stepIndex] !== undefined) fetchAndShow();
      });
    }

    const backBtn = document.getElementById('br-back');
    if (backBtn) backBtn.addEventListener('click', () => showStep(stepIndex - 1));
  }

  // ── Fetch + show ──────────────────────────────────────────────────
  async function fetchAndShow() {
    document.getElementById('br-question-area').style.display = 'none';
    document.getElementById('br-loading').style.display = 'flex';
    document.getElementById('br-progress').style.width = '100%';

    try {
      catalog = await fetchCatalog();
      const { results, alternatives, usedFallback } = findMatches();
      trackResponse(results, usedFallback);
      renderResults(results, alternatives, usedFallback);
    } catch (err) {
      console.error('[Bible Recommender] Error:', err);
      showError();
    }
  }

  // ── Render a single product card ─────────────────────────────────
  function renderCard(b, isHero, rank) {
    const conf = getConfidence(b.score);
    const badge = conf && !isHero
      ? `<div class="br__badge ${conf.cls}">${conf.label}</div>`
      : '';
    const reasonsHtml = b.reasons?.length
      ? `<ul class="br__reasons">${b.reasons.map(r => `<li>${r}</li>`).join('')}</ul>`
      : '';
    const imgHtml = b.image
      ? `<img class="br__product-img" src="${b.image}" alt="${b.title}" loading="lazy">`
      : `<div class="br__product-img br__product-img--placeholder"></div>`;
    const scoreHtml = isHero && b.score > 0
      ? `<div class="br__match-score">${b.score}% Match</div>`
      : '';

    return `
      <div class="br__product-card${isHero ? ' br__product-card--hero' : ''}">
        <div class="br__product-img-wrap">
          ${badge}
          ${imgHtml}
        </div>
        <div class="br__product-info">
          ${scoreHtml}
          <div class="br__product-title">${b.title}</div>
          <div class="br__product-price">&#8377;${b.price.toLocaleString('en-IN')}</div>
          ${reasonsHtml}
          <a class="br__product-btn" href="/products/${b.handle}"
             data-handle="${b.handle}" data-score="${b.score}" data-rank="${rank}">View Bible</a>
        </div>
      </div>`;
  }

  // ── Render results ────────────────────────────────────────────────
  function renderResults(bibles, alternatives, usedFallback) {
    document.getElementById('br-loading').style.display = 'none';
    document.getElementById('br-results').style.display = 'block';

    const noticeEl = document.getElementById('br-fallback-notice');
    if (usedFallback) noticeEl.classList.remove('hidden');
    else noticeEl.classList.add('hidden');

    const contentEl = document.getElementById('br-results-content');

    if (!bibles.length) {
      contentEl.innerHTML = `
        <div class="br__no-results">
          <p>We couldn't find a match — but we have plenty of Bibles to explore.</p>
          <a href="/collections/all-products" style="display:inline-block;margin-top:16px;text-decoration:none;font-weight:600;color:#C8102E;">
            Browse all Bibles →
          </a>
        </div>`;
      return;
    }

    const a = buildAnswerMap();
    const personality = getPersonality(a);
    const summaryPoints = buildSummaryPoints(a);
    const [best, ...others] = bibles;

    // 🎉 Celebration header
    const foundHtml = `
      <div class="br__found">
        <div class="br__found-emoji">🎉</div>
        <div class="br__found-personality">You're a <strong>${personality.type}</strong></div>
        <p class="br__found-desc">${personality.desc}</p>
      </div>`;

    // ✔ Answer summary
    const summaryHtml = summaryPoints.length ? `
      <div class="br__summary">
        <div class="br__summary-label">Based on your answers, you're looking for:</div>
        <ul class="br__summary-points">
          ${summaryPoints.map(p => `<li>${p}</li>`).join('')}
        </ul>
      </div>` : '';

    // 🥇 Hero recommendation
    const heroHtml = `
      <div class="br__section-label">🥇 Our recommendation</div>
      ${renderCard(best, true, 0)}`;

    // Secondary cards
    const secondaryHtml = others.length ? `
      <div class="br__section-label br__section-label--secondary">You might also like</div>
      <div class="br__products br__products--secondary">
        ${others.map((b, i) => renderCard(b, false, i + 1)).join('')}
      </div>` : '';

    // ── AI chat box (only if aiUrl is configured) ───────────────
    const chatHtml = CONFIG.aiUrl ? `
      <div class="br__chat br__chat--prominent">
        <div class="br__chat-heading">Tell us one more thing</div>
        <p class="br__chat-sub">Share any extra detail — poor eyesight, a special occasion, large print, a specific need — and we'll fine-tune your recommendation.</p>
        <div class="br__chat-input-row">
          <input class="br__chat-input" id="br-chat-input" type="text"
                 placeholder="e.g. I need large print, it's a birthday gift, I'm a new believer…" maxlength="280" autocomplete="off">
          <button class="br__chat-send" id="br-chat-send" type="button" aria-label="Send">→</button>
        </div>
        <div class="br__chat-response hidden" id="br-chat-response">
          <div class="br__chat-avatar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            </svg>
          </div>
          <div class="br__chat-text" id="br-chat-text"></div>
        </div>
      </div>` : '';

    // Chat sits between hero and secondary cards so it's seen before people scroll
    contentEl.innerHTML = foundHtml + summaryHtml + heroHtml + chatHtml + secondaryHtml;

    contentEl.querySelectorAll('.br__product-btn[data-handle]').forEach(link => {
      link.addEventListener('click', () => {
        trackEvent('product_clicked', {
          handle: link.dataset.handle,
          score: parseInt(link.dataset.score) || 0,
          rank: parseInt(link.dataset.rank) || 0,
        });
      });
    });

    if (CONFIG.aiUrl) {
      initChat({
        who: a.who || '',
        use: a.use || '',
        language: a.language || '',
        style: a.style || '',
        translation: a.translation || '',
        budget: a.budget || '',
        printSize: a.printSize || '',
        format: a.format || '',
        topResult: { title: best.title, score: best.score, reasons: best.reasons || [], handle: best.handle },
        alternatives: alternatives.map(b => ({ title: b.title, handle: b.handle, score: b.score, reasons: b.reasons || [] })),
      });
    }
  }

  // ── AI chat ───────────────────────────────────────────────────────
  function initChat(context) {
    const input = document.getElementById('br-chat-input');
    const sendBtn = document.getElementById('br-chat-send');
    const responseEl = document.getElementById('br-chat-response');
    const textEl = document.getElementById('br-chat-text');

    async function send() {
      const question = input.value.trim();
      if (!question || sendBtn.disabled) return;

      sendBtn.disabled = true;
      input.disabled = true;
      sendBtn.textContent = '…';

      responseEl.classList.remove('hidden');
      textEl.innerHTML = `
        <span class="br__chat-typing">
          <span></span><span></span><span></span>
        </span>`;

      try {
        const res = await fetch(CONFIG.aiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ context, question }),
        });
        const data = await res.json();
        textEl.innerHTML = '';

        // ── Needs list ──────────────────────────────────────────
        if (data.needs?.length) {
          const needsWrap = document.createElement('div');
          needsWrap.className = 'br__chat-needs';
          needsWrap.innerHTML = `
            <div class="br__chat-needs-label">Based on everything you've told us, you need:</div>
            <ul>${data.needs.map(n => `<li>${n}</li>`).join('')}</ul>`;
          textEl.appendChild(needsWrap);
        }

        // ── Main reply ──────────────────────────────────────────
        const replyText = document.createElement('p');
        replyText.className = 'br__chat-reply';
        replyText.textContent = data.reply || 'Sorry, something went wrong. Please try again.';
        textEl.appendChild(replyText);

        // ── Why it changed ──────────────────────────────────────
        if (data.whyChanged) {
          const why = document.createElement('p');
          why.className = 'br__chat-why';
          why.textContent = data.whyChanged;
          textEl.appendChild(why);
        }

        // ── Track the chat interaction ──────────────────────────
        trackChat(question, data);

        // ── Product link ────────────────────────────────────────
        const linked = data.alternativeHandle
          ? context.alternatives?.find(b => b.handle === data.alternativeHandle)
          : null;
        const product = linked || (data.alternativeHandle ? null : context.topResult);

        if (product?.handle) {
          const link = document.createElement('a');
          link.href = '/products/' + product.handle;
          link.textContent = 'View ' + (product.title || 'this Bible') + ' →';
          link.className = 'br__chat-link';
          textEl.appendChild(link);
        }
      } catch {
        textEl.textContent = 'Couldn\'t reach the advisor right now. Please try again.';
      }

      sendBtn.disabled = false;
      input.disabled = false;
      sendBtn.textContent = '→';
      input.value = '';
      input.placeholder = 'Ask another question…';
      input.focus();
    }

    sendBtn.addEventListener('click', send);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') send(); });
  }

  function showError() {
    document.getElementById('br-loading').style.display = 'none';
    document.getElementById('br-results').style.display = 'block';
    document.getElementById('br-results-content').innerHTML = `
      <div class="br__no-results">
        <p>Something went wrong. Please try refreshing the page.</p>
      </div>`;
  }

  // ── Restart ───────────────────────────────────────────────────────
  function restart() {
    answers = [];
    catalog = [];
    document.getElementById('br-results').style.display = 'none';
    document.getElementById('br-question-area').style.display = 'flex';
    showWelcome();
  }

  // ── Boot ──────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
